import { useCallback, useEffect, useRef, useState } from 'react';
import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';
import { partialEditor } from 'domains/reporter/RichTextEditor/utils';
import { logger } from 'modules/logger';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState/useReportLockedState';
import { useCurrentCaseReport } from './useCurrentCaseReport';
import type { ReportTemplateFieldsFragment } from 'generated/graphql';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  claimingWorklistSmidState,
  InsertedTemplateDataSource,
  reportTemplateState,
} from 'domains/reporter/Reporter/state';
import { useCurrentWorklistItems } from './useCurrentWorklistItems';
import { WithApolloContext } from 'modules/Apollo/types';
import { useSlate } from 'domains/reporter/RichTextEditor/core';
import { useTemplateInsertionState } from './useTemplateInsertionState';

export type AutoloadTemplateState = 'pending' | 'success' | 'skipped';

type UseAutoloadTemplateProps = WithApolloContext & {
  unfurlTemplate: (template: ReportTemplateFieldsFragment) => void;
};

export const useAutoloadTemplate = ({
  unfurlTemplate,
  context,
}: UseAutoloadTemplateProps): {
  autoloadTemplate: () => void;
  autoloadTemplateState: AutoloadTemplateState;
} => {
  const { isClaimedByMe, isAddendumStatus } = useReportLockedState({ context });
  const previousIsClaimedByMeRef = useRef(isClaimedByMe);
  const { currentCaseReport: worklistItem, loadingCaseReport } = useCurrentCaseReport();
  const editor = useSlate();
  const { loading: currentWorklistItemsLoading } = useCurrentWorklistItems();
  const claimingWorklistSmid = useRecoilValue(claimingWorklistSmidState);
  const setTemplate = useSetRecoilState(reportTemplateState);
  const { setTemplateInsertion } = useTemplateInsertionState();

  const [autoloadTemplateState, setAutoloadTemplateState] =
    useState<AutoloadTemplateState>('pending');

  const currentWorklistItemSmid = useRef<Nullable<string>>(null);

  useEffect(() => {
    if (worklistItem == null) return;

    // if window is reporter is still open, and the worklist item or claiming user has changed, reset autoloadTemplateState
    const worklistItemChanged = currentWorklistItemSmid.current !== worklistItem.smid;
    const claimantChanged = previousIsClaimedByMeRef.current !== isClaimedByMe;

    if (worklistItemChanged || claimantChanged) {
      const changeReason = worklistItemChanged ? 'worklist item' : 'claimant';

      if (worklistItemChanged) {
        logger.info(
          `[useAutoloadTemplate] resetting reportTemplateState to null due to ${changeReason} change`,
          {
            currentWorklistItemSmid: worklistItem.smid,
            previousWorklistItemSmid: currentWorklistItemSmid.current,
          }
        );
      }

      if (autoloadTemplateState !== 'pending') {
        logger.info(
          `[useAutoloadTemplate] resetting autoloadTemplateState to pending due to ${changeReason} change`
        );
        setAutoloadTemplateState('pending');
      }
      // Always clear template immediately on case/claim changes to avoid stale templates
      // from the previously viewed case being inserted while autoload is still pending.
      setTemplate(null);
    }

    currentWorklistItemSmid.current = worklistItem.smid;
    previousIsClaimedByMeRef.current = isClaimedByMe;
  }, [autoloadTemplateState, worklistItem, worklistItem?.smid, isClaimedByMe, setTemplate]);

  const autoloadTemplate = useCallback(() => {
    if (currentWorklistItemsLoading || loadingCaseReport) {
      return;
    }

    const isClaiming = worklistItem != null && claimingWorklistSmid === worklistItem.smid;
    if (isClaiming) {
      logger.info(
        '[useAutoloadTemplate] keeping autoloadTemplateState as pending while claim refetch is in-flight',
        {
          worklistItemSmid: worklistItem?.smid,
          claimingWorklistSmid,
          autoloadTemplateState,
        }
      );
      return;
    }

    if (isClaimedByMe === false) {
      setAutoloadTemplateState('skipped');
      logger.info(
        '[useAutoloadTemplate] resetting autoloadTemplateState to skipped due to not being claimed by user'
      );
      return;
    }

    if (worklistItem == null) {
      return;
    }

    if (isAddendumStatus) {
      setAutoloadTemplateState('skipped');
      logger.info(
        '[useAutoloadTemplate] resetting autoloadTemplateState to skipped due report being in addendum status'
      );
      return;
    }

    const { report } = worklistItem;
    if (report == null) {
      return;
    }

    const { created, sectionsLastUpdated, template } = report;

    if (editor == null || editor.children.length === 0) {
      return;
    }

    if (template == null || (sectionsLastUpdated != null && sectionsLastUpdated !== created)) {
      logger.info(
        '[useAutoloadTemplate] resetting autoloadTemplateState to skipped as there is no template to autoload',
        {
          worklistItemSmid: worklistItem.smid,
          isClaimedByMe,
          loadingCaseReport,
          currentWorklistItemsLoading,
          claimingWorklistSmid,
        }
      );
      setAutoloadTemplateState('skipped');
      return;
    }

    const autoloadInfoMessage = `[ReporterComponent] Attempting to autoload template "${
      template.id
    }" for worklist item "${worklistItem.smid}" with procedure description "${
      worklistItem.procedure?.description ?? 'N/A'
    }"`;

    logger.info(autoloadInfoMessage, {
      logId: editor.selection ? stringifyRange(editor.selection) : 'null',
      editor: partialEditor(editor),
      template: {
        name: template.name,
        smid: template.id,
      },
      updatedTime: sectionsLastUpdated,
    });

    unfurlTemplate(template);

    setTemplateInsertion({
      templateId: template.id,
      source: InsertedTemplateDataSource.Autoload,
      worklistItemSmid: worklistItem.smid,
    });

    setAutoloadTemplateState('success');
    /**
     * RP-3188: Similar to the invocation of `handleInsertTemplate` within the useEffect in Fields.Context
     * (see note), we don't want to re-run this effect when the editor or worklistItem changes.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClaimedByMe,
    currentWorklistItemsLoading,
    loadingCaseReport,
    claimingWorklistSmid,
    autoloadTemplateState,
    worklistItem,
    setTemplateInsertion,
    unfurlTemplate,
  ]);

  return {
    autoloadTemplate,
    autoloadTemplateState,
  };
};

export default useAutoloadTemplate;
